import { useMemo } from "react";

import { StoryCard } from "@/components/StoryCard";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";
import { getYouTubeEmbedUrl } from "@/utils/videoEmbed";

const formatDateTime = (value) => {
  if (!value) return "No time set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export const LivePage = ({ feed, siteData }) => {
  const stream = siteData?.featured_livestream;
  const relatedStories = useMemo(() => (feed || []).slice(0, 6), [feed]);
  const replayStories = useMemo(() => (feed || []).filter((story) => Boolean(story.video_url || story.permalink)).slice(0, 4), [feed]);
  const embedUrl = getYouTubeEmbedUrl(stream?.youtube_url);
  const isLiveNow = stream?.status === "live";

  usePageSeo({
    canonicalPath: "/live",
    description: "Watch the Content Watch HQ live desk with livestream coverage, scheduled takeovers, reporter updates, and replay archive links.",
    title: "Live | Content Watch HQ",
  });

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="live-page">
      <section className="grid gap-8 border border-[#D9DCE1] bg-[#0A0A0A] px-6 py-8 text-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] lg:grid-cols-[minmax(0,1fr)_320px] lg:px-8" data-testid="live-page-hero">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D21404]" data-testid="live-page-overline">{isLiveNow ? "Live now" : "Livestream center"}</p>
          <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-white sm:text-6xl" data-testid="live-page-title">{stream?.title || "Live Coverage"}</h1>
          <p className="mt-4 max-w-3xl text-base leading-8 text-slate-200" data-testid="live-page-description">{stream?.coverage_focus || stream?.description || "Livestreams, event coverage, and live reporter updates from the Content Watch HQ network."}</p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="live-page-status-row">
            <span className={`border px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] ${isLiveNow ? "border-white/15 bg-[#D21404] text-white" : "border-white/15 bg-white/5 text-white/85"}`} data-testid="live-page-status-value">{stream?.status || "scheduled"}</span>
            <span className="border border-white/15 bg-white/5 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white/85" data-testid="live-page-event-type">{stream?.event_type || "Breaking coverage"}</span>
            <span className="border border-white/15 bg-white/5 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white/85" data-testid="live-page-priority">Priority {stream?.takeover_priority || 1}</span>
          </div>
        </div>

        <div className="space-y-3" data-testid="live-page-hero-meta-panel">
          <div className="border border-white/10 bg-white/5 p-4" data-testid="live-page-next-window-card">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">Takeover window</p>
            <p className="mt-2 text-sm leading-7 text-white">{formatDateTime(stream?.takeover_window_start)} → {formatDateTime(stream?.takeover_window_end)}</p>
          </div>
          <div className="border border-white/10 bg-white/5 p-4" data-testid="live-page-reporter-panel">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">On-scene crew</p>
            <p className="mt-2 text-sm leading-7 text-white">{(stream?.reporters_on_scene || []).length ? stream.reporters_on_scene.join(", ") : "Desk assignment pending"}</p>
          </div>
        </div>
      </section>

      <section className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.25fr)_360px]" data-testid="live-page-main-grid">
        <div className="border border-[#D9DCE1] bg-white p-6" data-testid="live-page-player-panel">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]" data-testid="live-page-player-overline">Primary stream</p>
          <h2 className="mt-2 font-[Montserrat] text-2xl font-bold uppercase tracking-[0.08em] text-[#0A0A0A]" data-testid="live-page-player-title">{stream?.title || "Live desk preparing"}</h2>
          <p className="mt-4 text-sm leading-7 text-slate-700" data-testid="live-page-player-copy">{stream?.description || "Create or feature a livestream from the admin dashboard to display it here."}</p>
          <div className="mt-6 overflow-hidden border border-[#D9DCE1] bg-slate-950" data-testid="live-page-player-frame">
            {embedUrl ? (
              <div className="aspect-video w-full">
                <iframe allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="h-full w-full" data-testid="live-page-youtube-embed" src={embedUrl} title={stream?.title || "Live stream"} />
              </div>
            ) : (
              <div className="flex aspect-video items-center justify-center px-6 text-center text-sm leading-7 text-slate-200" data-testid="live-page-player-empty">Feature a YouTube livestream from the newsroom dashboard to activate the live player.</div>
            )}
          </div>
          {stream?.youtube_url ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Button asChild className="rounded-none bg-[#1338BE] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#102F9B]" data-testid="live-page-watch-youtube-button">
                <a href={stream.youtube_url} rel="noreferrer" target="_blank">Watch on YouTube</a>
              </Button>
            </div>
          ) : null}
        </div>

        <aside className="space-y-6" data-testid="live-page-sidebar">
          <div className="border border-[#D9DCE1] bg-white p-6" data-testid="live-page-upcoming-panel">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Upcoming events</p>
            <p className="mt-3 text-sm leading-7 text-slate-700" data-testid="live-page-upcoming-copy">{stream?.status === "scheduled" ? `${stream?.title || "Next live event"} is queued for ${formatDateTime(stream?.start_time || stream?.takeover_window_start)}.` : "The live desk is active right now. Any follow-up replay will appear below after the stream ends."}</p>
          </div>
          <div className="border border-[#D9DCE1] bg-white p-6" data-testid="live-page-status-panel">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]">Live reporter updates</p>
            <div className="mt-4 space-y-3" data-testid="live-page-updates-list">
              {(stream?.reporter_updates || ["The live desk is standing by for the next major Houston event."]).map((item, index) => (
                <p key={`${item}-${index}`} className="border border-[#D9DCE1] bg-[#F8F9FA] p-3 text-sm leading-7 text-slate-700" data-testid={`live-page-update-${index}`}>{item}</p>
              ))}
            </div>
          </div>
          <div className="border border-[#D9DCE1] bg-white p-6" data-testid="live-page-replay-panel">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#1338BE]">Replay archive</p>
            <div className="mt-4 space-y-3" data-testid="live-page-replay-list">
              {replayStories.map((story, index) => (
                <p key={story.slug} className="border border-[#D9DCE1] bg-[#F8F9FA] p-3 text-sm leading-7 text-slate-700" data-testid={`live-page-replay-${index}`}>{story.headline}</p>
              ))}
            </div>
          </div>
        </aside>
      </section>

      <section className="news-card-grid mt-8" data-testid="live-page-related-stories">
        {relatedStories.map((story) => <StoryCard key={story.id || story.slug} story={story} />)}
      </section>
    </main>
  );
};